import React, { useEffect, useReducer, useState } from 'react'
import { Form, Grid, Segment, Tab } from 'semantic-ui-react'
import * as R from 'ramda'

import BasicCustomStyle from 'components/BasicCustomStyle'
import { toast } from 'libs/toast'
import { getPreviewStyle, getWebEmbedPreviewStyle } from './utils'
import BrandWebSurveyStyle from 'components/BrandWebSurveyStyle'
import BrandWebEmbedSurveyStyle from 'components/BrandWebEmbedSurveyStyle'
import BrandWebSurveyLivePreview from 'components/BrandWebSurveyLivePreview'
import BrandWebEmbedSurveyLivePreview from 'components/BrandWebEmbedSurveyLivePreview'
import useSaveUserActivityLog, { ACCOUNT_ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import useUser from 'hooks/useUser'
import { validateRequiredText } from 'libs/form-validation'

const defaultBrandSetting = {
  name: null,
  customCSS: null,
  logoObject: {
    file: null,
    imagePreview: null,
  },
  backgroundColor: '#F4F4F4',
  backgroundImage: null,
  accentColor: '#017EFE',
  font: 'Lato',
  previewURL: '',
  revisionPreview: 1,
  web_embed_styles: {},
  web_embed_custom_css: null,
}
/**
 * @typedef {object} BrandSetting
 * @property {string} name
 * @property {string} customCSS
 * @property {string} backgroundColor
 * @property {string} backgroundImage
 * @property {string} accentColor
 * @property {string} font
 * @property {string} previewURL
 * @property {import('components/BasicCustomStyle').Logo} logoObject
 * @property {number} revisionPreview
 * @property {object} web_embed_styles
 * @property {string} web_embed_custom_css
 *
 * @typedef {object} Action
 * @property {string} type
 * @property {string=} fieldName
 * @property {string | BrandSetting} payload
 *
 * @param {BrandSetting} state
 * @param {Action} action
 * @returns
 */
function brandReducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD': {
      if (action.fieldName === 'previewURL') {
        return {
          ...state,
          revisionPreview: state.revisionPreview + 1,
          [action.fieldName]: action.payload,
        }
      } else {
        return {
          ...state,
          [action.fieldName]: action.payload,
        }
      }
    }
    case 'SET_WEB_EMBED_FIELD': {
      return {
        ...state,
        web_embed_styles: {
          ...state.web_embed_styles,
          // @ts-ignore
          [action.fieldName]: action.payload,
        },
      }
    }
    case 'SET_INITIAL_STATE': {
      return action.payload
    }

    default:
      return state
  }
}

/**
 * @typedef {object} Brand
 * @property {number} id
 * @property {string} name
 * @property {string=} logo
 * @property {string=} custom_css
 * @property {string} background_color
 * @property {string} accent_color
 * @property {string=} background_image
 * @property {string} font
 * @property {string=} survey_domain
 * @property {object=} web_embed_styles
 * @property {string=} web_embed_custom_css
 *
 * @param {object} props
 * @param {Brand} props.brand
 * @param {function} props.updateBrand
 * @param {boolean} props.enableCustomizeBranding
 * @param {boolean} props.isAccordionOpened
 * @param {boolean} props.hasManageBrandsPermission
 * @param {function} props.handleCustomCSSChange
 */
export default function BrandSettingForm({
  brand,
  updateBrand,
  enableCustomizeBranding,
  isAccordionOpened,
  hasManageBrandsPermission,
  handleCustomCSSChange,
}) {
  const [state, dispatch] = useReducer(brandReducer, defaultBrandSetting)
  const [activeTab, setActiveTab] = useState(0)
  const [nameError, setNameError] = useState('')
  const saveUserActivityLog = useSaveUserActivityLog()
  const userInfo = useUser()
  const companyId = R.path(['selected_workspace', 'id'], userInfo)

  const {
    name,
    customCSS,
    logoObject,
    backgroundColor,
    backgroundImage,
    accentColor,
    font,
    previewURL,
    revisionPreview,
  } = state

  useEffect(() => {
    if (brand) {
      dispatch({
        type: 'SET_INITIAL_STATE',
        payload: {
          name: brand.name,
          customCSS: brand.custom_css || '',
          logoObject: {
            file: null,
            imagePreview: brand.logo,
          },
          backgroundColor: brand.background_color || '#F4F4F4',
          backgroundImage: brand.background_image || null,
          accentColor: brand.accent_color || '#017EFE',
          font: brand.font || 'Lato',
          previewURL: process.env.REACT_APP_PREVIEW_SURVEY_URL,
          revisionPreview: 1,
          web_embed_styles: brand.web_embed_styles,
          web_embed_custom_css: brand.web_embed_custom_css,
        },
      })
    }
  }, [brand])

  function handleUpdateBrand() {
    const error = validateRequiredText('Brand name', name || '')
    if (error) {
      setNameError(error)
      return
    }

    const url = `brand/brands/${brand.id}/`
    const data = {
      name,
      font,
      background_color: backgroundColor,
      background_image: backgroundImage,
      accent_color: accentColor,
      custom_css: customCSS,
      web_embed_styles: state.web_embed_styles,
      web_embed_custom_css: state.web_embed_custom_css,
    }
    if (logoObject.file) {
      data['logo'] = logoObject.imagePreview
    }
    updateBrand({ url, data })
      .then((response) => {
        toast.success(`The brand "${data.name}" has been updated.`)
        saveUserActivityLog({
          action: ACCOUNT_ACTION_NAME.saved_brand,
          statusCode: response.status,
          requestBody: data,
          target: `com_${companyId}`,
        })
      })
      .catch((error) => {
        toast.error('Oops! Something went wrong. Please contact Simplesat support for help.')
        saveUserActivityLog({
          action: ACCOUNT_ACTION_NAME.saved_brand,
          statusCode: error.response.status,
          requestBody: data,
          errorResponseBody: error.response.data,
          target: `com_${companyId}`,
        })
      })
  }

  function handleReloadPreview(event) {
    event.preventDefault()

    const url = previewURL + '&revison=' + revisionPreview
    dispatch({
      type: 'SET_FIELD',
      fieldName: 'previewURL',
      payload: url,
    })
  }

  const surveyLogoUrl = logoObject.imagePreview
  const previewStyle = getPreviewStyle(
    accentColor,
    backgroundColor,
    backgroundImage,
    customCSS,
    font,
    surveyLogoUrl
  )

  const webEmbedPreviewStyle = getWebEmbedPreviewStyle(
    accentColor,
    font,
    surveyLogoUrl,
    state.web_embed_custom_css,
    state.web_embed_styles
  )

  const isUpdatingWebSurvey = activeTab === 0

  useEffect(() => {
    const hasCustomCSS = Boolean(customCSS || state.web_embed_custom_css)
    handleCustomCSSChange(hasCustomCSS)
  }, [customCSS, handleCustomCSSChange, state.web_embed_custom_css])

  return (
    <Form id={`brand-${brand.id}-form`} onSubmit={handleUpdateBrand}>
      <Form.Group grouped>
        <Grid columns="equal" stackable>
          <Grid.Column width={6}>
            <Segment>
              <BasicCustomStyle
                name={name}
                nameError={nameError}
                setName={(e) => {
                  if (nameError) {
                    setNameError('')
                  }
                  dispatch({
                    type: 'SET_FIELD',
                    fieldName: 'name',
                    payload: e.target.value,
                  })
                }}
                logoObject={logoObject}
                setLogoObject={(file) =>
                  dispatch({
                    type: 'SET_FIELD',
                    fieldName: 'logoObject',
                    payload: file,
                  })
                }
                accentColor={accentColor}
                setAccentColor={(color) =>
                  dispatch({
                    type: 'SET_FIELD',
                    fieldName: 'accentColor',
                    payload: color,
                  })
                }
                backgroundImage={backgroundImage}
                setBackgroundImage={(backgroundImage) =>
                  dispatch({
                    type: 'SET_FIELD',
                    fieldName: 'backgroundImage',
                    payload: backgroundImage,
                  })
                }
                font={font}
                setFont={(font) =>
                  dispatch({
                    type: 'SET_FIELD',
                    fieldName: 'font',
                    payload: font,
                  })
                }
                brandId={brand.id}
                hasManageBrandsPermission={hasManageBrandsPermission}
              />
            </Segment>
          </Grid.Column>
          <Grid.Column width={10}>
            <Tab
              menu={{ secondary: true, pointing: true }}
              panes={[
                {
                  menuItem: 'Web survey',
                  render: () => (
                    <Tab.Pane className="!p-0 !border-0">
                      <BrandWebSurveyStyle
                        enableCustomizeBranding={enableCustomizeBranding}
                        backgroundColor={backgroundColor}
                        setBackgroundColor={(color) =>
                          dispatch({
                            type: 'SET_FIELD',
                            fieldName: 'backgroundColor',
                            payload: color,
                          })
                        }
                        backgroundImage={backgroundImage}
                        setBackgroundImage={(backgroundImage) =>
                          dispatch({
                            type: 'SET_FIELD',
                            fieldName: 'backgroundImage',
                            payload: backgroundImage,
                          })
                        }
                        hasManageBrandsPermission={hasManageBrandsPermission}
                        customCSS={customCSS}
                        setCustomCSS={(customCSS) =>
                          dispatch({
                            type: 'SET_FIELD',
                            fieldName: 'customCSS',
                            payload: customCSS,
                          })
                        }
                        surveyDomain={brand.survey_domain}
                      />
                    </Tab.Pane>
                  ),
                },
                {
                  menuItem: 'Web-embed survey',
                  render: () => (
                    <Tab.Pane className="!p-0 !border-0">
                      <BrandWebEmbedSurveyStyle
                        enableCustomizeBranding={enableCustomizeBranding}
                        backgroundColor={R.path(['web_embed_styles', '--survey-background'], state)}
                        setBackgroundColor={(color) =>
                          dispatch({
                            type: 'SET_WEB_EMBED_FIELD',
                            fieldName: '--survey-background',
                            payload: color,
                          })
                        }
                        hasManageBrandsPermission={hasManageBrandsPermission}
                        customCSS={state.web_embed_custom_css}
                        setCustomCSS={(customCSS) =>
                          dispatch({
                            type: 'SET_FIELD',
                            fieldName: 'web_embed_custom_css',
                            payload: customCSS,
                          })
                        }
                      />
                    </Tab.Pane>
                  ),
                },
              ]}
              activeIndex={activeTab}
              onTabChange={(_, data) => {
                // @ts-ignore
                setActiveTab(data.activeIndex)
              }}
            />
          </Grid.Column>
        </Grid>

        {isAccordionOpened && (
          <React.Fragment>
            {isUpdatingWebSurvey ? (
              <BrandWebSurveyLivePreview
                key={brand.id}
                previewURL={previewURL}
                previewStyle={previewStyle}
                handleReloadPreview={handleReloadPreview}
                showDeviceSwitcher={true}
                showResetButton={true}
              />
            ) : (
              <BrandWebEmbedSurveyLivePreview previewStyle={webEmbedPreviewStyle} />
            )}
          </React.Fragment>
        )}
      </Form.Group>
    </Form>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/BrandSettings/BrandSettingForm.js